import { useEffect, useState } from 'react';
import usePersistedKeyValueStore from './usePersistedKeyValueStore';
import { v4 as uuid } from 'uuid';

export type UseScreenParamStorageWriterArgs = {
    onPersisted: (storageKey: string) => void;
};

export type UseScreenParamStorageWriterReturnValue<TDataType> = {
    writeValue: (value: TDataType) => void;
};

/**
 * Passing large amounts of data between screens cannot be done safely through url params.
 *
 * This hook provides a way to store data in a key-value store and pass the key as a url param.
 */
export const useScreenParamWriter = <TDataType>({
    onPersisted,
}: UseScreenParamStorageWriterArgs): UseScreenParamStorageWriterReturnValue<TDataType> => {
    const [key, setKey] = useState<string>(uuid());
    const keyParamOnPersisted = () => {
        onPersisted(key);
        setKey(uuid());
    };
    const { setValue } = usePersistedKeyValueStore<TDataType>({
        category: 'screenParam',
        key: key,
        expiration: 1000 * 60 * 60 * 24, // 24 hours
        onPersisted: keyParamOnPersisted,
    });

    return {
        writeValue: setValue,
    };
};

export type UseScreenParamStorageReaderArgs<TDataType> = {
    isDataValid: (data: unknown) => data is TDataType;
    onInvalidData: () => void;
    key: string;
};

export type UseScreenParamStorageReaderReturnValue<TDataType> = {
    value: TDataType | undefined;
    isLoading: boolean;
};

/**
 * This hook reads data from a key-value store using a key passed as a url param.
 *
 * If the data is not valid, the onInvalidData callback is called.
 */
export const useScreenParamReader = <TDataType>({
    isDataValid,
    onInvalidData,
    key,
}: UseScreenParamStorageReaderArgs<TDataType>): UseScreenParamStorageReaderReturnValue<TDataType> => {
    const { value, isLoading } = usePersistedKeyValueStore<TDataType>({
        category: 'screenParam',
        key,
    });

    useEffect(() => {
        if (isLoading) return;

        if (!isDataValid(value)) {
            onInvalidData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    return {
        value,
        isLoading: isLoading || value === undefined,
    };
};
